/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { Suspense } from 'react';
import { makeStyles, Tab } from '@material-ui/core';
import DraftGrid from 'components/draftgrid/DraftGrid';
import FOTable from 'components/foTable/FOTable';
import WelcomeSection from 'components/welcomeSection/WelcomeSection';
import TableActions from 'pages/draftManagement/components/TableActions';
import CustomMenu from 'components/common/CustomMenu';
import { useTranslation } from 'react-i18next';
import { TabContext, TabList } from '@material-ui/lab';
import { useHistory, useParams } from 'react-router-dom';

import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { isFeatureEnabled, ToggleableFeature } from '../../shared/utils/featuresUtils';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import useDraftManagement, { DRAFT_VIEW, DraftManagementContext } from './useDraftManagement';
import DraftManagementDialogs from './components/DraftManagementDialogs';
import useProcedure from '../../procedure/useProcedure';
import { LayoutSwitcher } from './components/LayoutSwitcher';
import { useSubsequentActions } from '../../shared/subsequentActions/useSubsequentActions';
import { RoutePath } from '../../shared/enums/RoutePath';

const useStyles = makeStyles(theme => ({
  submissionActionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    position: 'relative',
    height: 'auto',
  },
  buttons: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
    marginTop: '1.125rem',
  },
  tabList: {
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.tabSectionUnderline,
    borderBottomWidth: '1px',
  },
}));

export default function DraftManagement() {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const draftManagementHook = useDraftManagement();
  const {
    columns,
    rows,
    data,
    totalCount,
    loading,
    page,
    pageSize,
    updatePageAction,
    updatePageSizeAction,
    tableConfiguration,
    isAllSelected,
    handleSelectAll,
    getSortOrderForColumn,
    onClickColumn,
    isRowSelected,
    handleSelectRow,
    handleActionMenuClick,
    getActionsTooltipText,
    computeCombinedCellEntries,
    filter,
    setFilter,
    emptyTableData,
    selectedRow,
    anchorActionMenu,
    computeActionEntries,
    emptyTableWarningMessage,
    emptyTableSuggestion,
    error,
  } = draftManagementHook;

  const currentProcedure = useProcedure();
  const { initialSubmissionProcedure } = useParams();
  const { enabled: saEnabled, initialProcedure, resource: saProcedure } = useSubsequentActions(
    initialSubmissionProcedure ?? currentProcedure.id,
  );

  const isAddressBookPage = currentProcedure.category.includes('addressbook');
  const welcomeSectionData = {
    title: t(initialProcedure?.label) || (isAddressBookPage && t('address-book.title')) || '',
    description:
      t(initialProcedure?.description) || (isAddressBookPage && t('messages.address-book.description')) || '',
  };

  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const configurableHeaderEnabled = isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.HEADER);

  document.title = `${welcomeSectionData.title} - Patent E-filing`;

  return (
    <DraftManagementContext.Provider value={draftManagementHook}>
      <Suspense fallback={<div>Loading... </div>}>
        <WelcomeSection
          configurableHeaderEnabled={configurableHeaderEnabled}
          title={welcomeSectionData.title}
          description={welcomeSectionData.description}
        />

        {saEnabled && (
          <TabContext value={currentProcedure.id}>
            <div className={classes.tabList}>
              <TabList
                onChange={(e, procedure) =>
                  history.replace(
                    RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
                      procedure,
                      ...(procedure === saProcedure.id && { initialSubmissionProcedure: initialProcedure.id }),
                    }),
                  )
                }
                aria-label="Procedures"
                textColor="primary"
                indicatorColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                {initialProcedure.hideInitialSubmissions ? (
                  <div />
                ) : (
                  <Tab fullWidth label={t(initialProcedure.label)} value={initialProcedure.id} />
                )}
                <Tab fullWidth label={t(saProcedure.label)} value={saProcedure.id} />
              </TabList>
            </div>
          </TabContext>
        )}

        <section aria-label={t('aria.sectionname.drafts.actions')} className={classes.submissionActionsContainer}>
          <LayoutSwitcher key="layout-switcher" />
          <div className={classes.buttons}>{draftManagementHook.createButton}</div>
        </section>

        <section aria-label={t('aria.sectionname.drafts.table')} style={{ overflowX: 'auto' }}>
          <TableActions key="table-actions" />
          {draftManagementHook.view === DRAFT_VIEW.GRID && <DraftGrid />}
          {draftManagementHook.view === DRAFT_VIEW.TABLE && (
            <FOTable
              columns={columns}
              rows={rows}
              data={data}
              totalCount={totalCount}
              loading={loading}
              page={page}
              pageSize={pageSize}
              computeCombinedCellEntries={computeCombinedCellEntries}
              updatePageAction={updatePageAction}
              updatePageSizeAction={updatePageSizeAction}
              isAllSelected={isAllSelected}
              handleSelectAll={handleSelectAll}
              getSortOrderForColumn={getSortOrderForColumn}
              onClickColumn={onClickColumn}
              isRowSelected={isRowSelected}
              handleSelectRow={handleSelectRow}
              handleActionMenuClick={handleActionMenuClick}
              getActionsTooltipText={getActionsTooltipText}
              isAddressBookPage={isAddressBookPage}
              filter={filter}
              setFilter={setFilter}
              emptyTableData={emptyTableData}
              selectedRow={selectedRow}
              anchorActionMenu={anchorActionMenu}
              computeActionEntries={computeActionEntries}
              tableConfiguration={tableConfiguration}
              emptyTableWarningMessage={emptyTableWarningMessage}
              emptyTableSuggestion={emptyTableSuggestion}
              error={error}
            />
          )}
        </section>
      </Suspense>

      <>
        <CustomMenu
          aria-label={t('aria.sectionname.drafts.menu')}
          anchorEl={draftManagementHook.anchorActionMenu}
          item={draftManagementHook.selectedRow}
          onClose={draftManagementHook.handleActionMenuClose}
          entries={draftManagementHook.actionEntries}
        />
        <CustomMenu
          anchorEl={draftManagementHook.anchorViewMenu}
          item={draftManagementHook.selectedRow}
          onClose={draftManagementHook.handleViewMenuClose}
          entries={draftManagementHook.previewEntries(selectedRow)}
        />
      </>

      <DraftManagementDialogs {...draftManagementHook} />
    </DraftManagementContext.Provider>
  );
}
